import {ErrorWithContext, FormContainer, FormattedFormRow} from "@dvrt/graphql-widgets";
import {Target} from "./TargetSelector";
import {Link} from "@mui/material";
import TargetSelector from "./TargetSelector";
import {ReferenceRetarget} from "@/api";
import ErrorHelper from "@/utils/ErrorHelper";
import {defineMessage, defineMessages, MessageDescriptor} from "react-intl";

// Type aliases to make 'numbers' more readable
type Id = number;
type NumberId = number;

export type RelatedDataTarget = Target & {
    /** Value to compare against when excluding targets */
    exclusionValue?: any;
};

// TODO: create a summary enum to who can refer to who
/** Enum to determine what inputs do the RelatedDataResolver need */
export enum RelatedDataTargetType {
    EXTENSION,
    IVR,
    GROUP,
    FAX,
    GATEWAY,
}

export enum ReferenceType {
    PHONE_BUTTON,
    GATEWAY,
    IVR_ACTION,
    SECRETARY,
    OVERRIDE_CALLER,
}

/** These are all the fields that can possibly be present in the props as ReferenceSections */
const possibleFields = ["phoneButtons", "gateways", "ivrActions", "secretary", "overrideCaller"] as const;
type PossibleField = (typeof possibleFields)[number];

const FIELD_LABELS = defineMessages<PossibleField>({
    phoneButtons: {
        id: "related_data.labels.phone_buttons",
        defaultMessage: "Button {button} on a device of user {user}",
    },
    gateways: {id: "related_data.labels.gateways", defaultMessage: "Gateway {gateway}"},
    ivrActions: {
        id: "related_data.labels.ivr_actions",
        defaultMessage: "Button {action_path} in a menu of attendant {ivr}",
    },
    secretary: {
        id: "related_data.labels.secretary",
        defaultMessage: "Referred as secretary by user {user}",
    },
    overrideCaller: {
        id: "related_data.labels.override_caller",
        defaultMessage: "Referred as override caller by user {user}",
    },
});

/** Inner onChange function that handles the raw change in the data
 * so the final onChange function can be called with the desired type of data */
type _OnChange = (field: PossibleField, id: Id, numberId: NumberId) => void;

/** Type to display the references with information about them */
export type Reference<R extends ReferenceType> = {
    referrerId: Id;
    displayName: string;
    exclusionValues?: any[];
} & (R extends ReferenceType.PHONE_BUTTON
    ? {
          extensionId: Id;
          button: number;
      }
    : R extends ReferenceType.IVR_ACTION
      ? {
            ivrId: Id;
            profileName: string;
            actionPath: number[];
        }
      : object);

/** Reference sections for each reference type */
export type ReferenceSection<R extends ReferenceType> = {
    references: Reference<R>[] | undefined;
    targets: RelatedDataTarget[];
};

type Props<T extends RelatedDataTargetType, G extends RelatedDataResolution> = {
    domainId: Id;
    onChange: (resolution: G) => void;
    value: Record<string, ReferenceRetarget[]>;
    errorHelper: ErrorHelper;
} & (T extends RelatedDataTargetType.EXTENSION
    ? {
          phoneButtons: ReferenceSection<ReferenceType.PHONE_BUTTON>;
          gateways: ReferenceSection<ReferenceType.GATEWAY>;
          ivrActions: ReferenceSection<ReferenceType.IVR_ACTION>;
          secretary: ReferenceSection<ReferenceType.SECRETARY>;
          overrideCaller: ReferenceSection<ReferenceType.OVERRIDE_CALLER>;
      }
    : T extends RelatedDataTargetType.IVR
      ? {
            ivrActions: ReferenceSection<ReferenceType.IVR_ACTION>;
            gateways: ReferenceSection<ReferenceType.GATEWAY>;
            overrideCaller: ReferenceSection<ReferenceType.OVERRIDE_CALLER>;
        }
      : T extends RelatedDataTargetType.GROUP
        ? {
              ivrActions: ReferenceSection<ReferenceType.IVR_ACTION>;
              gateways: ReferenceSection<ReferenceType.GATEWAY>;
              overrideCaller: ReferenceSection<ReferenceType.OVERRIDE_CALLER>;
          }
        : T extends RelatedDataTargetType.FAX
          ? {
                ivrActions: ReferenceSection<ReferenceType.IVR_ACTION>;
                overrideCaller: ReferenceSection<ReferenceType.OVERRIDE_CALLER>;
            }
          : T extends RelatedDataTargetType.GATEWAY
            ? {
                  overrideCaller: ReferenceSection<ReferenceType.OVERRIDE_CALLER>;
              }
            : object);

/** Type to hold the state of the resolution temporary in this component */
type RelatedDataResolution = {
    [referenceType: string]: ReferenceRetarget[];
};

const ReferrerLink = ({path, displayName}: {path: string; displayName: string}) => (
    <Link style={{textDecoration: "none"}} href={path}>
        {displayName}
    </Link>
);

const getValuesForTranslation = (reference: Reference<any>, field: PossibleField, domainId: Id) => {
    switch (field) {
        case "phoneButtons":
            return {
                button: (reference as Reference<ReferenceType.PHONE_BUTTON>).button,
                user: (
                    <ReferrerLink
                        path={`/domains/${domainId}/users/${
                            (reference as Reference<ReferenceType.PHONE_BUTTON>).extensionId
                        }/devices#provisioned`}
                        displayName={reference.displayName}
                    />
                ),
            };
        case "ivrActions":
            return {
                action_path: (reference as Reference<ReferenceType.IVR_ACTION>).actionPath.join(" -> "),
                ivr: (
                    <ReferrerLink
                        path={`/domains/${domainId}/ivrs/${
                            (reference as Reference<ReferenceType.IVR_ACTION>).ivrId
                        }/profiles`}
                        displayName={reference.displayName}
                    />
                ),
            };
        case "gateways":
            return {
                gateway: (
                    <ReferrerLink
                        path={`/domains/${domainId}/gateways/gateways/${
                            (reference as Reference<ReferenceType.GATEWAY>).referrerId
                        }/settings`}
                        displayName={reference.displayName}
                    />
                ),
            };
        case "secretary":
        case "overrideCaller":
            return {
                user: (
                    <ReferrerLink
                        path={`/domains/${domainId}/users/${
                            (reference as Reference<ReferenceType.SECRETARY | ReferenceType.OVERRIDE_CALLER>).referrerId
                        }/generic`}
                        displayName={reference.displayName}
                    />
                ),
            };
    }
};

const RelatedDataField = ({
    field,
    section,
    domainId,
    value,
    _onChange,
    errorHelper,
}: {
    field: PossibleField;
    section: ReferenceSection<any>;
    value: ReferenceRetarget[];
    domainId: Id;
    _onChange: _OnChange;
    errorHelper: ErrorHelper;
}) => {
    const getCurrentSelectedTargetNumberId = (id: Id): NumberId | undefined => {
        const retarget = value.find((retarget) => retarget.referrerId === id);
        return retarget?.newTargetNumberId;
    };

    const getCurrentSelectedTarget = (numberId: NumberId): RelatedDataTarget | undefined => {
        return section.targets.find((target) => target.numberId === numberId);
    };

    const getErrorsForCurrentTarget = (id: Id): ErrorWithContext[] | undefined => {
        const currentTargetNumberId = getCurrentSelectedTargetNumberId(id);
        if (currentTargetNumberId === undefined) return undefined;
        const currentTarget = getCurrentSelectedTarget(currentTargetNumberId);
        if (currentTarget === undefined) return undefined;
        return errorHelper
            ?.fieldErrors(`relatedData.${field}`)
            ?.filter((error) => error.context["target"] === currentTarget.displayName);
    };

    return section?.references.length > 0 ? (
        <>
            {section.references.map((reference, index) => {
                const errorsForReference = getErrorsForCurrentTarget(reference.referrerId);
                let notSelectedText: MessageDescriptor | undefined = undefined;
                if (field === "phoneButtons") {
                    notSelectedText = defineMessage({
                        id: "extension.delete.references.remove-button",
                        defaultMessage: "Remove button",
                    });
                }
                return (
                    <FormattedFormRow
                        key={index}
                        label={FIELD_LABELS[field]}
                        labelValues={getValuesForTranslation(reference, field, domainId)}
                    >
                        <TargetSelector
                            key={`selector_${index}`}
                            selected={getCurrentSelectedTargetNumberId(reference.referrerId)}
                            targets={
                                reference.exclusionValues
                                    ? section.targets.filter((target) =>
                                          target.exclusionValue
                                              ? !reference.exclusionValues.includes(target.exclusionValue)
                                              : true,
                                      )
                                    : section.targets
                            }
                            handleChange={(newNumberId) => _onChange(field, reference.referrerId, newNumberId)}
                            errors={errorsForReference}
                            required={!notSelectedText}
                            notSelectedText={notSelectedText}
                        />
                    </FormattedFormRow>
                );
            })}
        </>
    ) : null;
};

/**
 * Component to resolve related data
 *
 * @param domainId - the domain id the endpoint is in
 * @param onChange - callback to pass the resolution data to the parent component
 * @param errorHelper - error helper to get the errors for the related data
 * @param rest - ReferenceSections depending on the type of the endpoint
 */
const RelatedDataResolver = <T extends RelatedDataTargetType, G extends RelatedDataResolution>(props: Props<T, G>) => {
    // underscored to indicate that this is not the props onChange
    const _onChange = (field: PossibleField, id: Id, numberId: NumberId) => {
        const changed: ReferenceRetarget[] = props.value[field].filter(
            (item: ReferenceRetarget) => item.referrerId !== id,
        );
        changed.push({
            referrerId: id,
            newTargetNumberId: numberId,
        });
        props.onChange({[field]: changed} as any);
    };

    return (
        // TODO: dvrt-graphql-widgets add plus properties
        <FormContainer style={{gridTemplateColumns: "auto 1fr"}}>
            {possibleFields
                .map((field, index) =>
                    (props as any)[field] !== undefined ? (
                        <RelatedDataField
                            key={index}
                            field={field}
                            section={(props as any)[field]}
                            value={props.value[field]}
                            domainId={props.domainId}
                            errorHelper={props.errorHelper}
                            _onChange={_onChange}
                        />
                    ) : undefined,
                )
                .filter((item) => item !== undefined)}
        </FormContainer>
    );
};

export default RelatedDataResolver;
